/**
 * Privacy / tracking consent without a cookie banner.
 *
 * Model:
 *   - Visitors outside consent-required regions are tracked by default
 *     (US opt-out model). They can opt out any time at /choices.
 *   - Visitors inside consent-required regions (EEA, UK, Switzerland,
 *     Quebec) get no analytics or advertising tags at all. Consent Mode
 *     stays denied and no Google script is loaded for them.
 *   - If the region cannot be resolved, we treat the visitor as if they
 *     were in a consent region and load nothing.
 *
 * The visitor's own opt-out always wins over the regional default.
 */

const OPTOUT_KEY = "privacy_optout";
const REGION_KEY = "visitor_region";

export const CONSENT_UPDATED_EVENT = "cookie-consent-updated";

/** EEA + UK + Switzerland + Iceland/Liechtenstein/Norway country codes. */
const CONSENT_COUNTRIES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE", "IS", "LI", "NO", "GB", "CH",
]);

export interface PrivacyOptOut {
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

/** What the visitor chose for themselves, if anything. */
export function getPrivacyOptOut(): PrivacyOptOut | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(OPTOUT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PrivacyOptOut;
  } catch {
    return null;
  }
}

/**
 * Record an opt-out (or a withdrawal of it). Stored on the device and
 * broadcast so tracking stops in this tab and any other open tab.
 */
export function setPrivacyOptOut(opts: { analytics: boolean; marketing: boolean }) {
  if (typeof window === "undefined") return;
  const value: PrivacyOptOut = { ...opts, timestamp: Date.now() };
  try {
    localStorage.setItem(OPTOUT_KEY, JSON.stringify(value));
  } catch {
    /* storage unavailable, the in-page event below still applies */
  }
  window.dispatchEvent(new CustomEvent(CONSENT_UPDATED_EVENT));
}

function cachedRegion(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(REGION_KEY);
  } catch {
    return null;
  }
}

/**
 * True when the visitor is in a region that requires opt-in consent, or when
 * their region is not known yet. Unknown always blocks.
 */
export function isConsentRegion(): boolean {
  const region = cachedRegion();
  if (!region) return true;
  return CONSENT_COUNTRIES.has(region) || region === "CA-QC";
}

let regionPromise: Promise<boolean> | null = null;

/**
 * Resolve the visitor's country from the same-origin Cloudflare trace
 * endpoint. Resolves to true when tracking is allowed for this visitor's
 * region. Cached for the browsing session.
 */
export function resolveRegion(): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (cachedRegion()) return Promise.resolve(!isConsentRegion());
  if (regionPromise) return regionPromise;

  regionPromise = (async () => {
    let loc = "";
    try {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 2000);
      const res = await fetch("/cdn-cgi/trace", { signal: controller.signal });
      window.clearTimeout(timer);
      if (res.ok) {
        const text = await res.text();
        loc = (/^loc=(.*)$/m.exec(text)?.[1] ?? "").trim().toUpperCase();
        const state = (/^region=(.*)$/m.exec(text)?.[1] ?? "").trim();
        if (loc === "CA" && /quebec/i.test(state)) loc = "CA-QC";
      }
    } catch {
      loc = "";
    }
    // Unknown, Tor exit node, or a failed lookup: treat as a consent region.
    if (!loc || loc === "XX" || loc === "T1") loc = "XX";
    try {
      sessionStorage.setItem(REGION_KEY, loc);
    } catch {
      /* ignore */
    }
    return !isConsentRegion();
  })();

  return regionPromise;
}

/** Analytics allowed: known non-consent region and no analytics opt-out. */
export function hasAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  if (isConsentRegion()) return false;
  return getPrivacyOptOut()?.analytics !== true;
}

/** Advertising allowed: known non-consent region and no marketing opt-out. */
export function hasMarketingConsent(): boolean {
  if (typeof window === "undefined") return false;
  if (isConsentRegion()) return false;
  return getPrivacyOptOut()?.marketing !== true;
}
